import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, Cloud, Package, Check } from 'lucide-react';
import { CityStop } from './MultiCitySelector';
import { format } from 'date-fns';

interface MultiCityPackingListProps {
  cities: CityStop[];
  packingData: Record<string, any>;
  onUpdateProgress: (cityId: string, categoryIndex: number, itemIndex: number, checked: boolean) => void;
}

export function MultiCityPackingList({ cities, packingData, onUpdateProgress }: MultiCityPackingListProps) {
  const [activeTab, setActiveTab] = useState(cities[0]?.id || '');

  useEffect(() => {
    if (cities.length > 0 && !activeTab) {
      setActiveTab(cities[0].id);
    }
  }, [cities, activeTab]);

  const getCityProgress = (cityId: string) => {
    const cityData = packingData[cityId];
    if (!cityData?.categories) return { packed: 0, total: 0, percentage: 0 };

    let packed = 0;
    let total = 0;

    cityData.categories.forEach((category: any) => {
      category.items.forEach((item: any) => {
        total++;
        if (item.packed) packed++;
      });
    });

    return {
      packed,
      total,
      percentage: total > 0 ? Math.round((packed / total) * 100) : 0
    };
  };

  const getOverallProgress = () => {
    let totalPacked = 0;
    let totalItems = 0;

    cities.forEach(city => {
      const progress = getCityProgress(city.id);
      totalPacked += progress.packed;
      totalItems += progress.total;
    });

    return {
      packed: totalPacked,
      total: totalItems,
      percentage: totalItems > 0 ? Math.round((totalPacked / totalItems) * 100) : 0
    };
  };

  const overall = getOverallProgress();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Multi-City Trip Progress
          </CardTitle>
          <CardDescription>
            Track packing for each destination
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Overall Progress</span>
              <span className="text-muted-foreground">{overall.packed} of {overall.total} items packed</span>
            </div>
            <div className="w-full bg-secondary rounded-full h-2.5">
              <div
                className="bg-primary h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${overall.percentage}%` }}
              />
            </div>
          </div>

          <div className="mt-4 grid gap-2">
            {cities.map((city, index) => {
              const progress = getCityProgress(city.id);
              return (
                <div key={city.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs">
                      Stop {index + 1}
                    </Badge>
                    <div>
                      <div className="font-medium text-sm">{city.destination}</div>
                      {city.arrivalDate && city.departureDate && (
                        <div className="text-xs text-muted-foreground">
                          {format(city.arrivalDate, 'MMM d')} - {format(city.departureDate, 'MMM d')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {progress.percentage}%
                    </span>
                    {progress.percentage === 100 && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${cities.length}, 1fr)` }}>
          {cities.map((city, index) => (
            <TabsTrigger key={city.id} value={city.id} className="text-xs">
              <MapPin className="h-3 w-3 mr-1" />
              {city.destination}
            </TabsTrigger>
          ))}
        </TabsList>

        {cities.map((city) => {
          const cityData = packingData[city.id];
          const progress = getCityProgress(city.id);

          return (
            <TabsContent key={city.id} value={city.id} className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{city.destination}, {city.country}</CardTitle>
                      <CardDescription>
                        {city.arrivalDate && city.departureDate ? (
                          `${format(city.arrivalDate, 'MMM d')} - ${format(city.departureDate, 'MMM d, yyyy')}`
                        ) : (
                          'Dates not set'
                        )}
                      </CardDescription>
                    </div>
                    <Badge variant={progress.percentage === 100 ? 'default' : 'secondary'}>
                      {progress.packed}/{progress.total} packed
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {cityData?.weather && (
                    <div className="mb-4 p-4 bg-muted rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Cloud className="h-4 w-4" />
                        <span className="font-medium text-sm">Weather Forecast</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {cityData.weather.condition} • {cityData.weather.temp}
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {cityData?.categories?.map((category: any, catIndex: number) => (
                      <div key={catIndex} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">{category.name}</h4>
                          <Badge variant="outline" className="text-xs">
                            {category.items.filter((i: any) => i.packed).length}/{category.items.length}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {category.items.map((item: any, itemIndex: number) => (
                            <div key={itemIndex} className="flex items-center gap-2 p-2 border rounded">
                              <Checkbox
                                checked={item.packed}
                                onCheckedChange={(checked) =>
                                  onUpdateProgress(city.id, catIndex, itemIndex, checked as boolean)
                                }
                              />
                              <span className={item.packed ? 'line-through text-muted-foreground' : ''}>
                                {item.name}
                              </span>
                              {item.quantity > 1 && (
                                <Badge variant="secondary" className="text-xs ml-auto">
                                  ×{item.quantity}
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
